import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Plus, Edit, Trash2,
  Award, Users, Wrench, Droplet, Zap, FileText,
  Shield, Star, CheckCircle, Clock, ShieldCheck, Leaf, ThumbsUp, Package,
} from 'lucide-react';
import { toast } from 'sonner';
import { getBenefits, adminCreateBenefit, adminUpdateBenefit, adminDeleteBenefit } from '../api/api';

// Choix d'icônes avec un libellé parlant. Les noms restent alignés avec
// iconMap dans components/Benefits.jsx (côté site public).
const ICONES = [
  { nom: 'ShieldCheck', label: 'Fiabilité', C: ShieldCheck },
  { nom: 'Clock', label: 'Rapidité', C: Clock },
  { nom: 'Award', label: 'Qualité', C: Award },
  { nom: 'Users', label: 'Équipe', C: Users },
  { nom: 'ThumbsUp', label: 'Satisfaction', C: ThumbsUp },
  { nom: 'CheckCircle', label: 'Garanti', C: CheckCircle },
  { nom: 'Leaf', label: 'Écologique', C: Leaf },
  { nom: 'Star', label: 'Excellence', C: Star },
  { nom: 'Wrench', label: 'Équipement', C: Wrench },
  { nom: 'Droplet', label: 'Produits', C: Droplet },
  { nom: 'Shield', label: 'Assurance', C: Shield },
  { nom: 'Package', label: 'Service complet', C: Package },
  { nom: 'Zap', label: 'Efficacité', C: Zap },
  { nom: 'FileText', label: 'Devis clair', C: FileText },
];
const ICON_MAP = Object.fromEntries(ICONES.map((i) => [i.nom, i.C]));

export const AdminBenefits = () => {
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', icon: 'ShieldCheck', order: 0 });

  useEffect(() => {
    fetchBenefits();
  }, []);

  const fetchBenefits = async () => {
    try {
      const data = await getBenefits();
      setBenefits(data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (benefit = null) => {
    if (benefit) {
      setEditingBenefit(benefit);
      setFormData({
        title: benefit.title,
        description: benefit.description,
        icon: benefit.icon,
        order: benefit.order,
      });
    } else {
      setEditingBenefit(null);
      setFormData({ title: '', description: '', icon: 'ShieldCheck', order: benefits.length + 1 });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingBenefit(null);
    setFormData({ title: '', description: '', icon: 'ShieldCheck', order: 0 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBenefit) {
        await adminUpdateBenefit(editingBenefit.id, formData);
        toast.success('Avantage mis à jour');
      } else {
        await adminCreateBenefit(formData);
        toast.success('Avantage créé');
      }
      handleCloseDialog();
      fetchBenefits();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet avantage ?')) return;
    try {
      await adminDeleteBenefit(id);
      toast.success('Avantage supprimé');
      fetchBenefits();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des avantages</h2>
          <p className="text-gray-600">Les raisons de choisir NETTO+, affichées sur la page d'accueil.</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-blue-600">
          <Plus className="w-4 h-4 mr-2" />
          Nouvel avantage
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {benefits.map((benefit) => {
          const IconeCarte = ICON_MAP[benefit.icon] || Award;
          return (
            <Card key={benefit.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-blue-600 p-3 rounded-xl">
                    <IconeCarte className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => handleOpenDialog(benefit)} className="p-2 bg-blue-50 rounded-lg hover:bg-blue-100">
                      <Edit className="w-4 h-4 text-blue-600" />
                    </button>
                    <button onClick={() => handleDelete(benefit.id)} className="p-2 bg-red-50 rounded-lg hover:bg-red-100">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{benefit.description}</p>
                <span className="text-xs text-gray-500">Ordre : #{benefit.order}</span>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingBenefit ? "Modifier l'avantage" : 'Nouvel avantage'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titre *</Label>
                <Input id="title" value={formData.title} onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="order">Ordre</Label>
                <Input id="order" type="number" value={formData.order} onChange={(e) => setFormData((prev) => ({ ...prev, order: parseInt(e.target.value) }))} required />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))} required rows={3} />
            </div>

            <div className="space-y-2">
              <Label>Icône</Label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {ICONES.map(({ nom, label, C }) => {
                  const actif = formData.icon === nom;
                  return (
                    <button
                      type="button"
                      key={nom}
                      onClick={() => setFormData((prev) => ({ ...prev, icon: nom }))}
                      className={`flex flex-col items-center gap-1.5 py-3 rounded-lg border transition-colors ${
                        actif ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-blue-300 text-gray-600'
                      }`}
                      aria-pressed={actif}
                    >
                      <C className="w-5 h-5" aria-hidden="true" />
                      <span className="text-xs">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>Annuler</Button>
              <Button type="submit" className="bg-blue-600">{editingBenefit ? 'Mettre à jour' : 'Créer'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
